import { Routes } from '@angular/router';
import { DashboardComponent } from './components/pages/dashboard/dashboard.component';
import { LoginComponent } from './components/pages/login/login.component';
import { AuthGuard } from './guards/auth.guard';
import { RoleGuard } from './guards/role.guard';
import { RestaurantStatusGuard } from './guards/restaurant-status.guard';
import { SubscriptionPlanGuard } from './guards/subscription-plan.guard';
import { ReservationGuard } from './guards/reservation.guard';
import { FEATURE_FLAG_KEYS } from './utils/feature-flag.util';
import { OrdersComponent } from './components/pages/orders/orders.component';
import { MenusComponent } from './components/pages/menus/menus.component';
import { TablesComponent } from './components/pages/tables/tables.component';
import { QrCodesComponent } from './components/pages/qr-codes/qr-codes.component';
import { AddTableComponent } from './components/pages/tables/add-table/add-table.component';
import { AddMenuComponent } from './components/pages/add-menu/add-menu.component';
import { SettingsComponent } from './components/pages/settings/settings.component';
import { StaffsComponent } from './components/pages/staffs/staffs.component';
import { StaffFormComponent } from './components/pages/staffs/staff-form/staff-form.component';
import { CategoryComponent } from './components/pages/category/category.component';
import { AddTypeComponent } from './components/pages/category/add-type/add-type.component';
import { AddCategoryComponent } from './components/pages/category/add-category/add-category.component';
import { AddOrderComponent } from './components/pages/add-order/add-order.component';
import { HomePageComponent } from './components/pages/home-page/home-page.component';
import { AddZoneComponent } from './components/pages/add-zone/add-zone.component';
import { ZoneFormComponent } from './components/pages/add-zone/zone-form/zone-form.component';
import { StationFormComponent } from './components/pages/station/station-form/station-form.component';
import { StaffPinFormComponent } from './components/pages/staffs/staff-pin-form/staff-pin-form.component';
import { RefreshComponent } from './components/pages/refresh/refresh.component';
import { NotificationsComponent } from './components/pages/notifications/notifications.component';
import { NotificationPreferencesComponent } from './components/pages/notifications/notification-preferences.component';
import { ChangePasswordComponent } from './components/pages/change-password/change-password.component';
import { ProfileComponent } from './components/pages/profile/profile.component';
import { PromotionsComponent } from './components/pages/promotions/promotions.component';
import { ServingTimeComponent } from './components/pages/serving-time/serving-time/serving-time.component';
import { RestaurantProfileComponent } from './components/pages/restaurant-profile/restaurant-profile/restaurant-profile.component';
import { SocialMediaComponent } from './components/pages/social-media/social-media.component';
import { FeatureFlagGuard } from './guards/feature-flag.guard';
import { SOCIAL_MEDIA_ROLES } from './utils/user-role.util';
import { StationComponent } from './components/pages/station/station.component';
import { DeviceComponent } from './components/pages/device/device.component';
import { CustomizationComponent } from './components/pages/customization/customization.component';
import { HardwareComponent } from './components/pages/hardware/hardware.component';
import { ReservationsComponent } from './components/pages/reservations/reservations.component';
import { ReservationSettingsComponent } from './components/pages/reservation-settings/reservation-settings.component';
import { IntegrationRedirectionComponent } from './components/pages/integration-redirection/integration-redirection/integration-redirection.component';
import { OfflineComponent } from './components/pages/offline/offline/offline.component';
import { SelectRestaurantComponent } from './components/pages/select-restaurant/select-restaurant.component';
import { TaxSettingComponent } from './components/pages/tax-setting/tax-setting.component';
import { ResetPasswordComponent } from './components/pages/reset-password/reset-password.component';
import { DeliveryPartnersComponent } from './components/pages/delivery-partners/delivery-partners.component';
import { UberEatsIntegrationRedirectionComponent } from './components/pages/uber-eats-integration-redirection/uber-eats-integration-redirection.component';
import { OrderFeedbackComponent } from './components/pages/order-feedback/order-feedback.component';
import { OrderPaymentComponent } from './components/pages/order-payment/order-payment.component';
import { ReportComponent } from './components/pages/report/report.component';
import { Dashboard2Component } from './components/pages/dashboard2/dashboard2.component';
import { DeactiveRestaurantComponent } from './components/pages/deactive-restaurant/deactive-restaurant.component';
import { SquareIntegrationRedirectionComponent } from './components/pages/square-integration-redirection/square-integration-redirection.component';
import { SubscriptionComponent } from './components/pages/subscription/subscription.component';
import { AppearanceComponent } from './components/pages/appearance/appearance.component';

export const routes: Routes = [
  { path: 'login', component: LoginComponent },
  { path: 'select-restaurant', component: SelectRestaurantComponent, canActivate: [AuthGuard] },
  { path: 'reset-password', component: ResetPasswordComponent, canActivate: [AuthGuard] },
  {
    path: 'deactive-restaurant/:status',
    component: DeactiveRestaurantComponent,
    canActivate: [AuthGuard],
  },
  { path: 'offline', component: OfflineComponent },
  /* Full-screen form pages: these carry their own header and deliberately
     render outside the sidebar/header shell. */
  { path: 'add-table', component: AddTableComponent, canActivate: [AuthGuard] },
  { path: 'edit-table/:id', component: AddTableComponent, canActivate: [AuthGuard] },
  { path: 'add-type', component: AddTypeComponent, canActivate: [AuthGuard] },
  { path: 'edit-type/:id', component: AddTypeComponent, canActivate: [AuthGuard] },
  { path: 'add-category/:typeId', component: AddCategoryComponent, canActivate: [AuthGuard] },
  { path: 'add-zone', component: ZoneFormComponent, canActivate: [AuthGuard] },
  { path: 'edit-zone/:id', component: ZoneFormComponent, canActivate: [AuthGuard] },
  { path: 'add-station', component: StationFormComponent, canActivate: [AuthGuard] },
  { path: 'edit-station/:id', component: StationFormComponent, canActivate: [AuthGuard] },
  { path: 'add-staff', component: StaffFormComponent, canActivate: [AuthGuard] },
  { path: 'edit-staff/:id', component: StaffFormComponent, canActivate: [AuthGuard] },
  { path: 'set-pin/:id', component: StaffPinFormComponent, canActivate: [AuthGuard] },
  {
    path: 'main',
    component: HomePageComponent,
    canActivate: [AuthGuard],
    canActivateChild: [RoleGuard, RestaurantStatusGuard, SubscriptionPlanGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        component: Dashboard2Component,
        data: { eyebrow: '', title: 'Home' },
      },
      {
        path: 'settings',
        component: SettingsComponent,
        // No flag of its own any more: each section (switches, To Go,
        // Payments) keeps its own rule inside the page.
        data: { eyebrow: 'Restaurant', title: 'Settings' },
      },
      {
        path: 'staffs',
        component: StaffsComponent,
        data: { eyebrow: 'Restaurant', title: 'Staff' },
      },
      {
        path: 'order',
        component: OrdersComponent,
        data: { eyebrow: 'Operations', title: 'Orders' },
      },
      {
        path: 'add-order',
        component: AddOrderComponent,
        data: { eyebrow: 'Operations', title: 'New Order' },
      },
      {
        path: 'menu',
        component: MenusComponent,
        data: { eyebrow: 'Menu', title: 'Menu' },
      },
      {
        path: 'add-menu',
        component: AddMenuComponent,
        data: { eyebrow: 'Menu', title: 'Add New Menu' },
      },
      {
        path: 'update-menu/:id',
        component: AddMenuComponent,
        data: { eyebrow: 'Menu', title: 'Update Menu' },
      },
      {
        path: 'type-and-categories',
        component: CategoryComponent,
        data: { eyebrow: 'Menu', title: 'Types & Categories' },
      },
      {
        path: 'table',
        component: TablesComponent,
        data: { eyebrow: 'Operations', title: 'Tables' },
      },
      {
        path: 'qr-codes',
        component: QrCodesComponent,
        data: { eyebrow: 'Operations', title: 'QR codes' },
      },
      { path: 'zone', component: AddZoneComponent, data: { eyebrow: 'Tables', title: 'Zones' } },
      {
        path: 'profile',
        component: ProfileComponent,
        data: { eyebrow: 'Settings', title: 'Profile & Account' },
      },
      {
        path: 'restaurant-profile',
        component: RestaurantProfileComponent,
        data: { eyebrow: 'Restaurant', title: 'Restaurant Profile' },
      },
      {
        // Fynkle Wall Profile and posts (FNF-1609). Behind the SOCIAL_MEDIA
        // flag and for admins, managers and super admins only; a restaurant
        // that isn't activated is kept out by RestaurantStatusGuard.
        path: 'social-media',
        component: SocialMediaComponent,
        canActivate: [FeatureFlagGuard],
        data: {
          eyebrow: 'Restaurant',
          title: 'Social Media',
          featureFlag: 'SOCIAL_MEDIA',
          roles: SOCIAL_MEDIA_ROLES,
        },
      },
      {
        path: 'change-password',
        component: ChangePasswordComponent,
        data: { eyebrow: 'Settings', title: 'Security' },
      },
      {
        path: 'notifications',
        component: NotificationsComponent,
        data: { eyebrow: 'Settings', title: 'Notifications' },
      },
      {
        path: 'notifications/preferences',
        component: NotificationPreferencesComponent,
        data: { eyebrow: 'Settings', title: 'Notifications' },
      },
      { path: 'refresh', component: RefreshComponent },
      {
        path: 'promotions',
        component: PromotionsComponent,
        data: { eyebrow: 'Operations', title: 'Promotions' },
      },
      {
        path: 'serving-time',
        component: ServingTimeComponent,
        data: { eyebrow: 'Restaurant', title: 'Serving Time' },
      },
      {
        path: 'station',
        component: StationComponent,
        data: { eyebrow: 'Restaurant', title: 'Stations' },
      },
      {
        path: 'device',
        component: DeviceComponent,
        data: { eyebrow: 'Restaurant', title: 'Devices' },
      },
      {
        path: 'customization',
        component: CustomizationComponent,
        data: { eyebrow: 'Menu', title: 'Customizations' },
      },
      {
        path: 'hardware',
        component: HardwareComponent,
        data: { eyebrow: 'Restaurant', title: 'Hardware' },
      },
      {
        path: 'reservation',
        component: ReservationsComponent,
        canActivate: [ReservationGuard],
        data: { eyebrow: 'Operations', title: 'Reservations' },
      },
      {
        path: 'reservation-settings',
        component: ReservationSettingsComponent,
        canActivate: [ReservationGuard],
        data: { eyebrow: 'Settings', title: 'Reservations' },
      },
      // To Go and Payments are sections of Settings now; old links land there.
      { path: 'togo', redirectTo: 'settings', pathMatch: 'full' },
      { path: 'payment', redirectTo: 'settings', pathMatch: 'full' },
      { path: 'tax', component: TaxSettingComponent, data: { eyebrow: 'Settings', title: 'Tax' } },
      {
        path: 'delivery-partners',
        component: DeliveryPartnersComponent,
        data: { eyebrow: 'Restaurant', title: 'Delivery Partners' },
      },
      {
        path: 'order-payment/:order_id',
        component: OrderPaymentComponent,
        data: { eyebrow: 'Operations', title: 'Order Payment' },
      },
      {
        path: 'order-feedback/:order_id',
        component: OrderFeedbackComponent,
        data: { eyebrow: 'Operations', title: 'Order Feedback' },
      },
      {
        path: 'report',
        component: ReportComponent,
        data: { eyebrow: 'Reports', title: 'Reports' },
      },
      {
        path: 'appearance',
        component: AppearanceComponent,
        data: { eyebrow: 'Settings', title: 'Appearance' },
      },
      {
        path: 'billing-and-subscription',
        component: SubscriptionComponent,
        data: { eyebrow: 'Finance', title: 'Billing & Subscription' },
      },
    ],
  },
  { path: '', redirectTo: '/main', pathMatch: 'full' },
  { path: 'integration-redirection', component: IntegrationRedirectionComponent },
  { path: 'uber-eats-integration-redirection', component: UberEatsIntegrationRedirectionComponent },
  { path: 'square-integration-redirection', component: SquareIntegrationRedirectionComponent },
];
